import React, { useState, useEffect } from "react";
import Pagination from "./Pagination";
import SearchBar from "./SearchBar/SearchBar.jsx";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { debounce } from "lodash";

const Table = ({
  columns = [],
  TableData = [],
  isDoubleClick,
  sortConfig,
  handleSort,
  myClass,
  pagination,
  isSearchBar,
  headingDataOnTable,
  TableButton,
}) => {
  const [filterData, setFilterData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Initialize and update filterData when TableData changes
  useEffect(() => {
    const debouncedSearch = debounce(() => {
      const originalData = Array.isArray(TableData) ? TableData : [];

      if (!searchQuery) {
        setFilterData(originalData);
      } else {
        const lowerQuery = searchQuery.toLowerCase();
        const filtered = originalData.filter((item) =>
          item &&
          Object.values(item).some((val) =>
            val?.toString().toLowerCase().includes(lowerQuery)
          )
        );
        setFilterData(filtered);
      }
    }, 300);

    debouncedSearch();

    return () => debouncedSearch.cancel();
  }, [searchQuery, TableData]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = Array.isArray(filterData)
    ? filterData.slice(indexOfFirstItem, indexOfLastItem)
    : [];
  const totalPages = Math.ceil((filterData?.length || 0) / itemsPerPage);

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key)
      return <FaSort className="ms-1 text-gray-500" />;
    return sortConfig.direction === "ascending" ? (
      <FaSortUp className="ms-1 text-gray-500" />
    ) : (
      <FaSortDown className="ms-1 text-gray-500" />
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const showTable = currentItems.length > 0;

  return (
    <div className="table-container">
      <div className="table-header">
        <div className="heading-title">{headingDataOnTable && headingDataOnTable}</div>

        <div className="table-actions">
          {isSearchBar && (
            <SearchBar setSearchQuery={setSearchQuery} searchQuery={searchQuery} />
          )}

          {TableButton}
        </div>
      </div>

      {!showTable ? (
        <div className="no-records">
          <p>No records found.</p>
        </div>
      ) : (
        <div className="table-wrapper table">
          <table className={`data-table ${myClass ? "hover " : ""}`}>
            <thead>
              <tr>
                {columns.map((col, index) => (
                  <th
                    key={index}
                    onClick={() =>
                      handleSort && col.key && handleSort(col.key)
                    }
                  >
                    {col.label}
                    {handleSort && col.key && getSortIcon(col.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: currentItems.length || 5 }).map((_, index) => (
                    <tr key={index} className="skeleton-row">
                      {columns.map((_, i) => (
                        <td key={i}>
                          <div className="skeleton-box"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                : currentItems.map((row, indx) => (
                    <tr
                      key={indx}
                      {...(isDoubleClick && {
                        onDoubleClick: () => isDoubleClick(row),
                      })}
                    >
                      {columns.map((col, colIndex) => (
                        <td key={colIndex}>
                          {col.render ? col.render(row) : <span>{row[col.key]}</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && filterData.length > 0 && (
        <Pagination
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          setItemsPerPage={setItemsPerPage}
          totalPages={totalPages}
          totalItems={filterData.length}
        />
      )}

      <style jsx>{`
        .table-container { 
          border-radius: 12px; 
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05); 
          padding: 10px 16px; 
          margin: 16px 0; 
          background: white;
        }
        .table-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-bottom: 16px; 
          flex-wrap: wrap;
          gap: 12px;
        }
        .heading-title {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
        }
        .table-actions { 
          display: flex; 
          gap: 12px; 
          align-items: center; 
          flex-wrap: wrap;
        }
        .table-wrapper { 
          overflow-x: auto; 
          max-height: 600px; 
          border-radius: 8px; 
          width: 100%; 
        }
        .data-table { 
          width: 100%; 
          border-collapse: separate; 
          border-spacing: 0; 
        }
        .data-table thead { 
          position: sticky; 
          top: 0; 
          background: #f8fafc; 
          z-index: 1; 
        }
        .data-table th { 
          padding: 12px 16px; 
          font-size: 13px; 
          font-weight: 600; 
          color: #1e293b; 
          text-transform: uppercase; 
          letter-spacing: 0.02em; 
          border-bottom: 2px solid #e2e8f0; 
          white-space: nowrap; 
          cursor: pointer; 
        }
        .data-table th:hover { 
          background: #edf2f7; 
        }
        .data-table td { 
          padding: 12px 16px; 
          font-size: 14px; 
          color: #2d3748; 
          border-bottom: 1px solid #edf2f7; 
          word-break: break-word; 
          white-space: normal; 
        }
        .data-table tr { 
          transition: background 0.2s; 
        }
        .data-table tr:hover { 
          background: #f1f5f9; 
        }
        .no-records { 
          display: flex; 
          justify-content: center; 
          align-items: center; 
          height: 200px; 
          color: #94a3b8; 
          font-size: 16px; 
          font-weight: 500; 
        }
        .skeleton-row td { 
          padding: 12px 16px; 
        }
        .skeleton-box { 
          height: 20px; 
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); 
          background-size: 200% 100%; 
          animation: skeleton-loading 1.5s infinite; 
          border-radius: 4px; 
        }
        @keyframes skeleton-loading { 
          0% { background-position: 200% 0; } 
          100% { background-position: -200% 0; } 
        }
        @media (max-width: 768px) {
          .table-header {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default Table;
